import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useMemo,
} from 'react';
import { gekkoApi } from '../../api/GekkoApi';

// Types based on OpenAPI schema
interface DiseaseStub {
  id: number;
  name: string;
  info: string;
  only_female: boolean;
  only_male: boolean;
  only_neutered: boolean;
  only_non_neutered: boolean;
  has_treatments: boolean;
  area_specified: boolean;
}

interface DiseaseCause {
  id: number;
  name: string;
}

interface Source {
  id: number;
  name: string;
}

interface Disease extends DiseaseStub {
  how_to_confirm: string;
  how_to_exclude: string;
  treatment_info: string;
  follow_up: string;
  prognosis: string;
  disease_causes: DiseaseCause[];
  sources: Source[];
}

interface DiseasesContextType {
  diseases: Map<number, Disease>;
  loading: Set<number>;
  error: string | null;
  fetchDisease: (diseaseId: number) => Promise<Disease | null>;
  getDisease: (diseaseId: number) => Disease | null;
  isLoading: (diseaseId: number) => boolean;
  clearError: () => void;
}

const DiseasesContext = createContext<DiseasesContextType | undefined>(
  undefined
);

interface DiseasesProviderProps {
  children: ReactNode;
}

export const DiseasesProvider: React.FC<DiseasesProviderProps> = ({
  children,
}) => {
  const [diseases, setDiseases] = useState<Map<number, Disease>>(new Map());
  const [loading, setLoading] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);

  // Memoize the api instance to prevent unnecessary re-renders
  const api = useMemo(() => gekkoApi, []);

  const fetchDisease = useCallback(
    async (diseaseId: number): Promise<Disease | null> => {
      // Return cached disease if available
      if (diseases.has(diseaseId)) {
        return diseases.get(diseaseId) || null;
      }

      // Don't fetch if already loading
      if (loading.has(diseaseId)) {
        return null;
      }

      try {
        setLoading((prev) => new Set([...prev, diseaseId]));
        setError(null);

        const disease = await api.get<Disease>(`/diseases/${diseaseId}`);

        setDiseases((prev) => new Map([...prev, [diseaseId, disease]]));
        return disease;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch disease';
        setError(errorMessage);
        console.error('Error fetching disease:', err);
        return null;
      } finally {
        setLoading((prev) => {
          const newSet = new Set(prev);
          newSet.delete(diseaseId);
          return newSet;
        });
      }
    },
    [diseases, loading, api]
  );

  const getDisease = useCallback(
    (diseaseId: number): Disease | null => {
      return diseases.get(diseaseId) || null;
    },
    [diseases]
  );

  const isLoading = useCallback(
    (diseaseId: number): boolean => {
      return loading.has(diseaseId);
    },
    [loading]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: DiseasesContextType = {
    diseases,
    loading,
    error,
    fetchDisease,
    getDisease,
    isLoading,
    clearError,
  };

  return (
    <DiseasesContext.Provider value={value}>
      {children}
    </DiseasesContext.Provider>
  );
};

export const useDiseases = (): DiseasesContextType => {
  const context = useContext(DiseasesContext);
  if (context === undefined) {
    throw new Error('useDiseases must be used within a DiseasesProvider');
  }
  return context;
};

// Export types for use in components
export type { Disease, DiseaseStub, DiseaseCause, Source };
